import { useQuery } from '@tanstack/react-query';
import { GetCompanyParentageResponse } from '@upright/common/model/input/companies/relations/api-types';
import { chunkedAwaitMap } from '@upright/common/utils';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { fetchApiWithError } from '@upright/ui/api/common/fetchers/fetch-with-error';
import { postToApi } from '@upright/ui/api/common/fetchers/request-api';
import { ApiResponse } from '@upright/ui/api/common/types';
import {
  AddISINsRequest,
  ChangeDraftModeRequest,
  ChangeDraftModeResponse,
  ChangeHideAllResultsRequest,
  ChangeHideAllResultsResponse,
  ChangeModellingStatusRequest,
  ChangeModellingStatusResponse,
  CheckISINConflictsResponse,
  CreateCompanyRequest,
  CreateCompanyResponse,
  DeleteSecurityIdsBulkRequest,
  DeleteSecurityIdsBulkResponse,
  EditCompanyRequest,
  EditCompanyResponse,
  GetCompaniesResponse,
  GetCompanyNamesResponse,
  GetCompanyResponse,
} from '../../../../../common/model/input/companies/api-types';
import {
  SecurityIdentifierSource,
  SecurityIdentifierType,
  SecurityType,
} from '../../../../../common/model/input/companies/security-identifier/types';
import { Company } from '../../../../../common/model/input/companies/types';
import { SuccessOrError } from '../../../../../common/types-api';

async function fetchCompanyOrThrow(id: string) {
  return await fetchOrThrow<GetCompanyResponse>(`/company/${id}`);
}

export function useCompany<TData = GetCompanyResponse>(
  companyId: string | undefined,
  options?: {
    select?: (data: GetCompanyResponse) => TData;
    isEnabled?: boolean | undefined;
    companyIdOverride?: string | undefined;
  },
) {
  return useQuery<GetCompanyResponse, ErrorWithHttpStatus, TData>({
    queryKey: ['getCompany', companyId],
    queryFn: async () => {
      if (!companyId) throw Error('Unexpected.');
      return await fetchCompanyOrThrow(companyId);
    },
    enabled: !!companyId && (options?.isEnabled ?? true),
    select: options?.select,
  });
}

export function useGetCompanies<T = GetCompaniesResponse>(
  companyIds: string[] | undefined | null,
  options?: {
    select?: (data: GetCompaniesResponse) => T;
    isEnabled?: boolean | undefined;
  },
) {
  return useQuery({
    queryKey: ['getCompany', companyIds],

    queryFn: async () => {
      if (!companyIds) throw Error('Unexpected.');
      return await chunkedAwaitMap(companyIds, fetchCompanyOrThrow);
    },

    enabled: !!companyIds && (options?.isEnabled ?? true),
    select: options?.select,
  });
}

export async function createCompany(data: CreateCompanyRequest) {
  return await postToApi<CreateCompanyResponse>(`/company`, data);
}

export async function editCompany(id: string, data: EditCompanyRequest) {
  return await fetchApiWithError<EditCompanyResponse>(`/company/${id}`, 'PATCH', data);
}

export async function addISINsToCompany(id: string, ISINs: string[], securityType?: SecurityType) {
  const requestData: AddISINsRequest = { ISINs, securityType };
  return await fetchApiWithError<Company>(`/company/${id}/ISINs`, 'POST', requestData);
}

export async function checkISINConflicts(id: string, ISINs: string[]) {
  return await fetchApiWithError<CheckISINConflictsResponse>(`/company/${id}/ISINs/get-conflicts`, 'POST', ISINs);
}

export async function deleteISINFromCompany(id: string, ISIN: string) {
  return await fetchApiWithError<Company>(`/company/${id}/ISIN`, 'DELETE', ISIN);
}

export async function deleteSecurityIdsFromCompanyBulk(
  companyId: string,
  types: SecurityIdentifierType[],
  sources: SecurityIdentifierSource[],
) {
  const requestData: DeleteSecurityIdsBulkRequest = {
    types,
    sources,
  };
  return await fetchApiWithError<DeleteSecurityIdsBulkResponse>(
    `/company/${companyId}/security-ids-bulk`,
    'DELETE',
    requestData,
  );
}

export async function deleteCompany(id: string) {
  return await fetchOrThrow<SuccessOrError>(`/company/${id}`, 'DELETE');
}

export async function fetchCompanyLegalNames(companyIds: string[]) {
  return await fetchOrThrow<GetCompanyNamesResponse>(`/companies/legal-names`, 'POST', { ids: companyIds });
}

export async function fetchCompanyParentage(companyId: string) {
  return await fetchOrThrow<GetCompanyParentageResponse>(`/company/${companyId}/parentage`, 'GET');
}

export async function changeModellingStatus(
  companyId: string,
  body: ChangeModellingStatusRequest,
): Promise<ApiResponse<ChangeModellingStatusResponse>> {
  return await fetchApiWithError(`/company/${companyId}/isModelled`, 'PATCH', body);
}

// PLT-4790: PLG Enterprise "Draft mode" toggle. Backend handler is gated on
// `requireTeamUser`, so a customer-side call will be rejected with 401.
export async function changeDraftMode(
  companyId: string,
  body: ChangeDraftModeRequest,
): Promise<ApiResponse<ChangeDraftModeResponse>> {
  return await fetchApiWithError(`/company/${companyId}/draftMode`, 'PATCH', body);
}

// PLT-5806: PLG "Hide all results" toggle. Backend handler is gated on
// `requireTeamUser`, so a customer-side call will be rejected with 401.
export async function changeHideAllResults(
  companyId: string,
  body: ChangeHideAllResultsRequest,
): Promise<ApiResponse<ChangeHideAllResultsResponse>> {
  return await fetchApiWithError(`/company/${companyId}/hideAllResults`, 'PATCH', body);
}
